import { useState, useEffect, useRef } from 'react';
import { toast } from "react-toastify";

// Services
import { deleteDomain, getDomainById, getDomains } from "../../service/api/domainService";
import { getTotalManagerCount } from "../../service/api/domainUserService";

// Composants
import DomainCard from "./DomainCard";
import DomainSearch from "./DomainSearch";
import DomainPagination from "./DomainPagination";
import EmptyState from "./EmptyState";
import DomainForm from "./DomainForm";
import ListeManagers from "./ListeManagers";
import DomainDetailsModal from "./DomainDetailsModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import './style/domains.css';


/**
 * COMPOSANT PRINCIPAL LISTE DES DOMAINES
 * Gère l'affichage et la gestion de tous les domaines
 */
const ListDomains = () => {
  // ÉTATS PRINCIPAUX
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États de recherche et pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  // États des modals
  const [showFormModal, setShowFormModal] = useState(false);
  const [showManagersModal, setShowManagersModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // États des données
  const [domainToEdit, setDomainToEdit] = useState(null);
  const [selectedDomainId, setSelectedDomainId] = useState(null);
  const [domainToDeleteId, setDomainToDeleteId] = useState(null);
  const [domainDetails, setDomainDetails] = useState(null);
  const [managerCounts, setManagerCounts] = useState({});

  // RÉFÉRENCES ET CONSTANTES
  const prevDomainsRef = useRef([]);
  const domainsPerPage = 6;

  /**
   * EFFET: Chargement périodique des domaines
   */
  useEffect(() => {
    const fetchDomains = async (isSilent = false) => {
      if (!isSilent) {
        setLoading(true);
        setError(null);
      }
      
      try {
        const data = await getDomains();
        if (JSON.stringify(data) !== JSON.stringify(prevDomainsRef.current)) {
          prevDomainsRef.current = data;
          setDomains(data);
        }
      } catch (error) {
        if (!isSilent) {
          setError(error.message);
          toast.error('Erreur lors du chargement des domaines.');
        }
      } finally {
        if (!isSilent) {
          setLoading(false);
        }
      }
    };

    // Appel initial
    fetchDomains(false);
    
    // Actualisation silencieuse
    const intervalId = setInterval(() => fetchDomains(true), 5000);
    return () => clearInterval(intervalId);
  }, []);

  /**
   * EFFET: Chargement des comptes de managers
   */
  useEffect(() => {
    const fetchManagerCounts = async () => {
      if (domains.length === 0) return;
      
      try {
        const counts = {};
        const promises = domains.map(domain => getTotalManagerCount(domain.id));
        const results = await Promise.all(promises);
        
        domains.forEach((domain, index) => {
          counts[domain.id] = results[index] || 0;
        });
        
        setManagerCounts(counts);
      } catch (err) {
        console.error("Erreur lors du chargement des comptes managers:", err);
      }
    };

    fetchManagerCounts();
    const interval = setInterval(fetchManagerCounts, 5000);
    return () => clearInterval(interval);
  }, [domains]);

  /**
   * GESTIONNAIRES D'ACTIONS
   */

  // Édition de domaine
  const handleEditClick = (domain) => {
    setDomainToEdit(domain);
    setShowFormModal(true);
  };

  // Suppression de domaine
  const handleDeleteClick = (domainId) => {
    setDomainToDeleteId(domainId);
    setShowDeleteModal(true);
  };

  // Confirmation de suppression
  const handleConfirmDelete = async () => {
    try {
      await deleteDomain(domainToDeleteId);
      toast.success("Domaine supprimé avec succès !");
      setDomains(prev => prev.filter(domain => domain.id !== domainToDeleteId));
    } catch (error) {
      toast.error("Erreur lors de la suppression du domaine.");
      console.error("Delete error:", error);
    } finally {
      setShowDeleteModal(false);
      setDomainToDeleteId(null);
    }
  };

  // Visualisation des managers
  const handleViewManagers = (domain) => {
    setSelectedDomainId(domain.id);
    setShowManagersModal(true);
  };

  // Visualisation des détails
  const handleViewDetails = async (domain) => {
    try {
      const details = await getDomainById(domain.id);
      setDomainDetails(details);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error("Erreur lors du chargement des détails du domaine.");
    }
  };

  // Entrée dans un domaine
  const handleEnterDomain = (event, domain) => {
    event.preventDefault();
    sessionStorage.setItem("selectedDomain", JSON.stringify(domain));
    sessionStorage.setItem("userRole", "manager");
    window.location.href = "/Trade-Dashboard-manager";
  };

  // Fermeture des modals
  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setDomainToEdit(null);
  };

  const handleCloseManagersModal = () => {
    setShowManagersModal(false);
    setSelectedDomainId(null);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setDomainDetails(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDomainToDeleteId(null);
  };

  /**
   * CALCULS POUR L'AFFICHAGE
   */
  const filteredDomains = domains.filter(domain =>
    domain.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastDomain = currentPage * domainsPerPage;
  const indexOfFirstDomain = indexOfLastDomain - domainsPerPage;
  const currentDomains = filteredDomains.slice(indexOfFirstDomain, indexOfLastDomain);
  const totalPages = Math.ceil(filteredDomains.length / domainsPerPage);

  /**
   * GESTIONNAIRES D'INTERFACE
   */
  const handleSearchChange = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  /**
   * RENDU DU COMPOSANT
   */
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
        <span className="ms-3">Chargement des domaines...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger text-center" role="alert">
        <strong>Erreur :</strong> {error}
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* En-tête avec recherche */}
      <DomainSearch
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onReset={handleReset}
      />

      {/* Grille des domaines */}
      <div className="row">
        {currentDomains.length > 0 ? (
          currentDomains.map((domain) => (
            <DomainCard
              key={domain.id}
              domain={domain}
              managerCount={managerCounts[domain.id] || 0}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              onViewManagers={handleViewManagers}
              onViewDetails={handleViewDetails}
              onEnterDomain={handleEnterDomain}
            />
          ))
        ) : (
          <EmptyState 
            hasSearch={searchTerm.length > 0} 
            onReset={handleReset} 
          />
        )}
      </div>

      {/* Pagination */}
      {filteredDomains.length > 0 && (
        <DomainPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredDomains.length}
          itemsPerPage={domainsPerPage}
          onPageChange={handlePageChange}
        />
      )}

      {/* MODALS */}

      {/* Modal de formulaire */}
      {showFormModal && (
        <DomainForm
          domain={domainToEdit}
          onClose={handleCloseFormModal}
          onSuccess={handleCloseFormModal}
        />
      )}

      {/* Modal des managers */}
      {showManagersModal && (
        <ListeManagers
          domainId={selectedDomainId}
          onClose={handleCloseManagersModal}
        />
      )}

      {/* Modal des détails */}
      {showDetailsModal && domainDetails && (
        <DomainDetailsModal
          domain={domainDetails}
          onClose={handleCloseDetailsModal}
        />
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && (
        <DeleteConfirmationModal
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isDeleting={false}
        />
      )}
    </div>
  );
};

export default ListDomains;